import React, { useState, useEffect } from 'react';
import type { InfoCardMode } from '@arkturian/wave-engine/core/EngineRuntime';
import { EngineRuntime } from '@arkturian/wave-engine/core/EngineRuntime';

const runtime = EngineRuntime.getInstance();

/** Floating mode switcher button — rendered outside Canvas as DOM overlay */
const InfoCardModeSwitcher: React.FC = () => {
    const modes: InfoCardMode[] = ['texture', 'css3d', 'screen'];
    const labels: Record<InfoCardMode, string> = { texture: 'TEX', css3d: '3D', screen: '2D' };
    const [mode, setMode] = useState<InfoCardMode>('screen');
    const [open, setOpen] = useState(false);

    const switchMode = (m: InfoCardMode) => {
        runtime.infoCardMode = m;
        setMode(m);
        setOpen(false);
        // Force re-render of canvas components
        window.dispatchEvent(new CustomEvent('infocard-mode', { detail: m }));
    };

    // Listen for mode changes from canvas
    useEffect(() => {
        const handler = (e: Event) => setMode((e as CustomEvent).detail);
        window.addEventListener('infocard-mode', handler);
        return () => window.removeEventListener('infocard-mode', handler);
    }, []);

    return (
        <div style={{
            position: 'fixed', top: '12px', right: '12px', zIndex: 9999,
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px',
        }}>
            <button
                onClick={() => setOpen(!open)}
                style={{
                    width: '36px', height: '36px', borderRadius: '50%',
                    background: 'rgba(59,130,246,0.4)', border: '1px solid rgba(59,130,246,0.6)',
                    color: '#f1f5f9', fontSize: '10px', fontWeight: 700,
                    fontFamily: 'monospace', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backdropFilter: 'blur(8px)', boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                }}
            >
                {labels[mode]}
            </button>
            {open && (
                <div style={{
                    background: 'rgba(10,15,30,0.95)', border: '1px solid rgba(59,130,246,0.3)',
                    borderRadius: '8px', padding: '4px', display: 'flex', flexDirection: 'column', gap: '2px',
                    backdropFilter: 'blur(8px)', boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                }}>
                    {modes.map(m => (
                        <button
                            key={m}
                            onClick={() => switchMode(m)}
                            style={{
                                background: m === mode ? 'rgba(59,130,246,0.4)' : 'transparent',
                                border: 'none', color: '#e2e8f0', padding: '6px 12px',
                                borderRadius: '4px', cursor: 'pointer', fontSize: '11px',
                                fontFamily: 'monospace', textAlign: 'left', whiteSpace: 'nowrap',
                            }}
                        >
                            {labels[m]} — {m === 'texture' ? 'Z-Buffer' : m === 'css3d' ? 'Interactive 3D' : 'Screen Overlay'}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default InfoCardModeSwitcher;
